import React from "react";
import { observer } from "mobx-react";
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  PodRevenueSortBy,
  PodRevenueSortType,
} from "../../../../../../models/Dashboard/PodRevenue/PodRevenueFilters";
import { PodRevenueOrderRow } from "../../../../../../models/Dashboard/PodRevenue/PodRevenueSummary";
import {
  formatPodCount,
  formatPodDateTime,
  formatPodMoney,
} from "./podRevenueFormat";
import { PodRevenueEmptyState, podHeaderCellSx } from "./PodRevenueSection";
import PodRevenuePager, { PodRevenueTableProgress } from "./PodRevenuePager";

interface Props {
  rows: PodRevenueOrderRow[];
  page: number;
  pages: number;
  total?: number;
  loading: boolean;
  error: string | null;
  sortBy: PodRevenueSortBy;
  sortType: PodRevenueSortType;
  onSort: (sort: PodRevenueSortBy) => void;
  onPage: (page: number) => void;
  onRetry?: () => void;
  onRowClick?: (row: PodRevenueOrderRow) => void;
  maxHeight?: number;
}

const COLS: Array<{
  id: string;
  label: string;
  sort?: PodRevenueSortBy;
  align?: "left" | "center" | "right";
}> = [
  { id: "order", label: "Order", align: "left" },
  { id: "cart", label: "Cart", align: "center" },
  { id: "customer", label: "Customer", align: "center" },
  { id: "store", label: "Store", align: "center" },
  { id: "units", label: "Units", sort: "units", align: "center" },
  {
    id: "selling_exclusive",
    label: "Selling excl.",
    sort: "selling_exclusive",
    align: "center",
  },
  { id: "gst", label: "GST", sort: "gst", align: "center" },
  { id: "handling", label: "Handling", sort: "handling", align: "center" },
  { id: "shipping", label: "Shipping", sort: "shipping", align: "center" },
  {
    id: "gross_revenue",
    label: "Gross",
    sort: "gross_revenue",
    align: "center",
  },
  { id: "date", label: "Date", sort: "order_date", align: "center" },
];

const num = { fontVariantNumeric: "tabular-nums" as const };

/** Order-level listing: one row per order, full order amounts. Click a row for its products. */
const PodRevenueOrdersTable: React.FC<Props> = observer((props) => {
  const {
    rows,
    page,
    pages,
    total,
    loading,
    error,
    sortBy,
    sortType,
    onSort,
    onPage,
    onRetry,
    onRowClick,
    maxHeight = 480,
  } = props;
  const colors = useTheme().palette.customColors;
  const cols = COLS;
  const cellSx = { borderColor: colors.lightGray, whiteSpace: "nowrap" as const };

  if (error)
    return <PodRevenueEmptyState error message={error} onRetry={onRetry} />;
  if (rows.length === 0 && !loading)
    return <PodRevenueEmptyState message="No orders found for the selected filters." />;

  const showSkeleton = loading && rows.length === 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: colors.lightGray,
        overflow: "hidden",
      }}
    >
      <PodRevenueTableProgress loading={loading && !showSkeleton} />
      <TableContainer
        sx={{
          maxHeight,
          overflowX: "auto",
          opacity: loading && !showSkeleton ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: "max-content" }}>
          <TableHead>
            <TableRow>
              {cols.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align ?? "left"}
                  sortDirection={
                    col.sort && sortBy === col.sort
                      ? sortType === "DESC"
                        ? "desc"
                        : "asc"
                      : false
                  }
                  sx={{
                    ...podHeaderCellSx,
                    whiteSpace: "nowrap",
                    color: colors.greyTextColor,
                    bgcolor: colors.white,
                    borderColor: colors.lightGray,
                  }}
                >
                  {col.sort ? (
                    <TableSortLabel
                      active={sortBy === col.sort}
                      direction={
                        sortBy === col.sort && sortType === "ASC"
                          ? "asc"
                          : "desc"
                      }
                      onClick={() => onSort(col.sort as PodRevenueSortBy)}
                      sx={
                        col.align === "center"
                          ? {
                              justifyContent: "center",
                              width: "100%",
                              position: "relative",
                              "& .MuiTableSortLabel-icon": {
                                position: "absolute",
                                right: 0,
                              },
                            }
                          : undefined
                      }
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {cols.map((c) => (
                      <TableCell key={c.id} align={c.align ?? "left"} sx={cellSx}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow
                    key={row.order_id}
                    hover
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      cursor: onRowClick ? "pointer" : undefined,
                      "&:nth-of-type(even)": {
                        bgcolor: colors.softCardBackground,
                      },
                      transition: "background-color .12s ease",
                    }}
                  >
                    <TableCell sx={cellSx}>
                      <CustomTypography
                        variant="heading7"
                        fontWeight={600}
                        color={colors.navyText}
                        sx={num}
                      >
                        #{row.order_id}
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {row.cart_id || "—"}
                    </TableCell>
                    <TableCell align="center" sx={cellSx}>{row.customer_name || "—"}</TableCell>
                    <TableCell align="center" sx={cellSx}>{row.store_name || "—"}</TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodCount(row.unit_count)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.selling_exclusive)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.gst_amount)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.handling_amount)}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodMoney(row.shipping_amount)}
                    </TableCell>
                    <TableCell align="center" sx={cellSx}>
                      <CustomTypography
                        variant="heading7"
                        fontWeight={700}
                        color={colors.navyText}
                        sx={num}
                      >
                        {formatPodMoney(row.gross_revenue)}
                      </CustomTypography>
                    </TableCell>
                    <TableCell align="center" sx={{ ...cellSx, ...num }}>
                      {formatPodDateTime(row.order_date)}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PodRevenuePager
        page={page}
        pages={pages}
        loading={loading}
        onPage={onPage}
        summary={total != null ? `${formatPodCount(total)} orders` : undefined}
      />
    </Paper>
  );
});

export default PodRevenueOrdersTable;
